// ask_user tool — AI emit khi thiếu info để hỏi lại user.
//
// Pattern:
//   1. AI emit `ask_user({question: "..."})`
//   2. Tool execute() gọi `askUser(question)` → Promise pending
//   3. Plugin set pendingQuestion → ChatPanel render bubble + accept user input
//   4. User reply → ChatPanel call submitAnswer(text) → Promise resolves
//   5. Tool returns `{user_answer: text}` → AI tiếp tục với info đầy đủ
//
// Plugin owns Tool definition để app khác reuse mà không phải copy schema.

import { useAgentUi } from "./use-agent-ui";

/// Shape của tool — match SDK Tool (name + description + parameters + execute).
/// App spread vào Tool[] cùng risk: "low".
export interface AskUserToolDef {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<unknown>;
}

/// Factory không phụ thuộc hook — app có thể inject askUser fn từ ngoài component.
export function createAskUserTool(
  askUser: (question: string) => Promise<string>,
): AskUserToolDef {
  return {
    name: "ask_user",
    description:
      "Ask the user a clarifying question when you don't have enough info to call another tool. " +
      "Use khi user prompt thiếu required arg (vd 'tạo group' không nói tên) hoặc ambiguous " +
      "(vd 'xoá Z1' nhưng có nhiều Z1). KHÔNG dùng default/đoán giá trị — luôn hỏi user. " +
      "Tool sẽ pause flow + chờ user reply trong chat → return { user_answer } để bạn dùng ở step tiếp.",
    parameters: {
      type: "object",
      properties: {
        question: {
          type: "string",
          description:
            "Câu hỏi tiếng Việt, ngắn gọn, cụ thể. Nêu rõ field cần info + options nếu ambiguous.",
        },
      },
      required: ["question"],
    },
    execute: async (args: Record<string, unknown>) => {
      const q = typeof args.question === "string" ? args.question.trim() : "";
      if (!q) {
        throw new Error("ask_user requires non-empty 'question' arg");
      }
      const answer = await askUser(q);
      return { user_answer: answer };
    },
  };
}

/// Hook variant — gọi trong Solid component setup. Tự lấy askUser từ context.
/// Return value stable across renders (askUser fn là stable trong provider).
export function useAskUserTool(): AskUserToolDef {
  const { askUser } = useAgentUi();
  return createAskUserTool(askUser);
}
